import { useState, useEffect, useRef } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

/**
 * Detects DevTools opening via window size differential.
 * Only active when isViewer is true (demo mode).
 * Logs detection events to the backend.
 */
export function useDevToolsDetection(isViewer: boolean): boolean {
  const [devToolsOpen, setDevToolsOpen] = useState(false);
  const loggedRef = useRef(false);

  useEffect(() => {
    if (!isViewer) return;

    const check = () => {
      const widthDiff = window.outerWidth - window.innerWidth > 160;
      const heightDiff = window.outerHeight - window.innerHeight > 160;
      const open = widthDiff || heightDiff;
      setDevToolsOpen(open);

      // Log the first detection to backend
      if (open && !loggedRef.current) {
        loggedRef.current = true;
        logSecurityEvent('devtools_open');
      }
      if (!open) {
        loggedRef.current = false;
      }
    };

    const interval = setInterval(check, 1000);
    check(); // initial check

    return () => clearInterval(interval);
  }, [isViewer]);

  // Monitor screen capture API
  useEffect(() => {
    if (!isViewer) return;
    if (!navigator.mediaDevices?.getDisplayMedia) return;

    const original = navigator.mediaDevices.getDisplayMedia.bind(navigator.mediaDevices);
    navigator.mediaDevices.getDisplayMedia = async function (...args: any[]) {
      logSecurityEvent('screen_capture_attempt');
      return original(...args);
    };

    return () => {
      navigator.mediaDevices.getDisplayMedia = original;
    };
  }, [isViewer]);

  return devToolsOpen;
}

function logSecurityEvent(eventType: string) {
  const token = sessionStorage.getItem('prism_token') || localStorage.getItem('prism_token');
  if (!token) return;

  fetch(`${API_BASE_URL}/auth/demo/security-event`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ type: eventType }),
  }).catch(() => {
    // Non-critical, don't surface errors
  });
}
